/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, useState, useEffect } from "react";

import { MessageList } from "react-chat-elements";
import "react-chat-elements/dist/main.css";

import type { MessageType } from "react-chat-elements";

// antd:
import { Avatar, Input, Space, Button, Modal, notification } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
// import DivE from "../react/DivE.tsx";
// import SpanE from "../react/SpanE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getTheme, getAllApps, getAppState, changeAppState, getUser, getSafePtr } from "../../libs/reducer.ts"; // getAllApps
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions, Tuser} from "../../types/easyTypes.ts";
import type {TuiState} from "../../types/uiapp.d.ts";

const { rz, $ } = window;

type Tcomponent = {
  readOnly?:boolean,
  type:"input" | "textarea",
  size?:"large" | "middle" | "small",
  autoSize?:{minRows:number, maxRows:number} | undefined,
  allowClear?:boolean,
  variant?:"outlined" | "borderless" | "filled",
  status?:"error" | "warning",
  maxLength?:number,
  enterWithCtrlToSend?:boolean
};

const ErrorContent:FC = (msg:string) => (
  <div className="uk-flex uk-flex-center uk-flex-middle" style={{height: "100%"}}>
    <h2 className="uk-margin-remove uk-text-center txt-white txt-stroke-red-shadow">{msg}</h2>
  </div>
);

const getFullPathFromKey = (path:string, appstate:TuiState) => {
  let fullPath = path;
  if (!~fullPath.indexOf("/")) {
    let fnd = false; const allStateKeys = rz.objKeys(appstate);
    for (let i = 0; i < allStateKeys.length; i++) {
      // eslint-disable-next-line prefer-destructuring
      if (appstate[allStateKeys[i]].key === path) { fnd = true; fullPath = appstate[allStateKeys[i]].fullPath; break }
    }
    if (!fnd) { return path }
  }
  return fullPath;
};

const RZChat:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZChat.displayName = "RZChat";
  const dispatch = useAppDispatch();
  const allApps = useAppSelector(getAllApps);
  const user = useAppSelector(getUser);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const dct = useAppSelector(getDict);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key));
  const elState = appState[fullPath];
  const {color, backgroundColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const chatType = useMemo<Tcomponent>(() => {
    const typ:Tcomponent = {readOnly: false, type: "textarea", size: "middle", allowClear: false, variant: "outlined", autoSize: undefined};
    (type || "").toLowerCase().replace((/(\s+|,|\|)/igm), ";").split(";").forEach((tt) => {
      // if (~["horizontal", "vertical"].indexOf(tt)) { typ.direction = tt }
      if (~["readonly"].indexOf(tt)) { typ.readOnly = true }
      else if (~["small", "middle", "large"].indexOf(tt)) { typ.size = tt }
      else if (tt === "autosize") { typ.autoSize = { minRows: 2, maxRows: 6 } }
      else if (~["input", "textarea"].indexOf(tt)) { typ.type = tt }
      else if (~["allowclear", "clearbutton"].indexOf(tt)) { typ.allowClear = true }
      else if (~["noborders", "borderless"].indexOf(tt)) { typ.variant = "borderless" }
      else if (~["filled"].indexOf(tt)) { typ.variant = "filled" }
      else if (~["error", "warning"].indexOf(tt)) { typ.status = tt }
      else if (tt === "enterwithctrltosend") { typ.enterWithCtrlToSend = true }
    });

    if (attributes?.readOnly || attributes?.readonly) { typ.readOnly = ((attributes.readOnly || attributes.readonly) === "true") }
    if (attributes?.size) { typ.size = attributes.size }
    if (attributes?.variant) { typ.variant = attributes.variant }
    if (attributes?.allowclear || attributes?.allowClear) { typ.allowClear = ((attributes?.allowclear || attributes?.allowClear) === "true") }

    if (attributes?.textarea === "true") { typ.type = "textarea" }
    if (attributes?.input === "true") { typ.type = "input" }
    if (attributes?.maxLength || attributes?.maxlength) { typ.maxLength = Number(attributes.maxLength || attributes.maxlength) }
    if (attributes?.enterWithCtrlToSend) { typ.enterWithCtrlToSend = (attributes.enterWithCtrlToSend === "true") }

    if (attributes?.autosize === "true") { typ.autoSize = { minRows: 2, maxRows: 6 } }
    else if ((attributes?.autosize || "").startsWith("{") && (attributes?.autosize || "").endsWith("}")) {
      const jsnb = (rz.strReplaceAll(attributes.autosize, "'", "\"")).trim(); let jsn; try { jsn = JSON.parse(jsnb) } catch (err) { console.log(err) }
      typ.autoSize = { minRows: jsn?.minRows || jsn?.minrows || 2, maxRows: jsn?.maxRows || jsn?.maxrows || 6 }
    }

    return typ;
  }, [type, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  const [errMsg, setErrMsg] = useState("");
  // Get all users
  const [allUsers, setAllUsers] = useState<Array<Tuser>>([]);
  useEffect(() => {
    if (allUsers.length || errMsg) { return }
    if (elState.specialParams && elState.specialParams.allUsers) { setAllUsers(elState.specialParams.allUsers); return }
    rz.serverAction({appkey: rootAttributes.key, action: "loadAuxiliaryDBs", params: {dbs: ["users"]}}).then((ans) => {
      // @ts-ignore
      if (ans && ans.users && ans.users.length) { setAllUsers(ans.users) }
      else { setErrMsg(ans?.err || " ") }
    });
  }, [allUsers.length, elState.specialParams, errMsg, rootAttributes.key]);

  // Get user roles
  const userRoles = useMemo<Array<string>>(() => {
    const curApp = (allApps || fnc.memorizedValues.apps).find((aa) => (aa.key === rootAttributes?.key));
    if (!curApp) { return [] }
    return fnc.getUserRolesForApp(user, curApp);
  }, [allApps, rootAttributes?.key, user]);

  const openid = useMemo(() => {
    if (elState?.specialParams && elState.specialParams?.openid) { return elState.specialParams.openid }
    const prefix = key.split("-")[0] + "-"; if (!rz.arrContains(["vf-", "ef-", "cf-"], prefix)) { return "" }
    const pth = getFullPathFromKey(prefix + "_id", appState); if (!pth || !appState[pth]) { return "" }
    return appState[pth].value || "";
  }, [appState, elState.specialParams, key]);

  const recipients = useMemo<Array<Tuser>>(() => {
    let rcpts:Array<Tuser> = [];
    if (elState?.specialParams && elState.specialParams?.recipients && rz.isArray(elState.specialParams.recipients)) {
      const uniqueStr = rz.arrGetUnique(elState.specialParams.recipients);
      rcpts = (allUsers || []).filter((uu) => (rz.arrContains(uniqueStr, uu.login, true, true)));
    }
    else if (attributes.recipients && rz.isString(attributes.recipients)) {
      (attributes.recipients.replace((/(\s+|,|\|)/igm), ";").split(";")).forEach((ff:string) => {
        if (ff === "curUser") {
          if (!~rz.arrFindObj(rcpts, {login: user.login})) { rcpts.push(rz.objClone(user)) }
        }
        else {
          const pth = getFullPathFromKey(ff, appState);
          if (pth && appState[pth]) {
            const el = appState[pth]; let logins:Array<string> = [];
            if (rz.arrContains(["approval", "chat"], el.name) || (el.name === "aggrid" && ~el.key.indexOf("history"))) {
              logins = (el.data || []).map((dd) => (dd.user)).filter((aa) => (!~rz.arrFindObj(rcpts, {login: aa})));
            }
            else if (el.name === "select") {
              if (rz.isArray(el.value)) { logins = el.value.filter((aa) => (!~rz.arrFindObj(rcpts, {login: aa}))) }
              else if (rz.isString(el.value) && !~rz.arrFindObj(rcpts, {login: el.value})) { logins.push(el.value) }
            }
            (allUsers || []).forEach((uu) => { if (rz.arrContains(logins, uu.login, true, true)) { rcpts.push(rz.objClone(uu)) } });
          }
        }
      });
    }
    return rcpts;
  }, [elState.specialParams, attributes.recipients, allUsers, user, appState]);

  const IamRecipient = useMemo(() => (rz.arrContains((recipients || []).map((ii) => (ii.login)), user.login, true, true)), [recipients, user.login]);

  // Get user roles
  // const userRoles = useMemo<Array<string>>(() => {
  //   const curApp = (allApps || fnc.memorizedValues.apps).find((aa) => (aa.key === rootAttributes?.key));
  //   if (!curApp) { return [] }
  //   return fnc.getUserRolesForApp(user, curApp);
  // }, [allApps, rootAttributes?.key, user]);

  const messageListReferance = React.createRef();

  const [inputText, setInputText] = useState("");

  // Единая точка применения результата пользовательского обработчика.
  // Chat сам обновляет свой data при отправке/удалении, а обработчик может дополнительно вернуть state
  // для соседних элементов демо или бизнес-формы; здесь этот state аккуратно докидывается в Redux.
  const applyClientState = useCallback((ret:any) => {
    if (ret && ret.err) { console.log(ret.err) }
    if (ret && ret?.state?.fullPath && ret?.state?.newState) {
      dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
    }
  }, [dispatch, rootAttributes.key]);

  const onInputChange = useCallback((event:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = event.target.value;
    if (inputValue !== inputText) { setInputText(inputValue) }
  }, [inputText]);

  const onInputBlur = useCallback((event:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = event.target.value;
    if (inputValue !== inputText) { setInputText(inputValue) }
  }, [inputText]);

  const onSubmitNewMessage = useCallback(() => {
    if (!inputText || !inputText.trim()) { return }
    // Apply changes to state:
    const newRows = rz.objClone(elState.data) || []; newRows.unshift({text: inputText.trim(), user: user.login, strDate: rz.dateToString(new Date(), "YYYY-MM-DD hh:mm:ss")});

    // @ts-ignore
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {data: newRows, loading: false, value: null}}));
    if (fnc.config?.isDev) { console.log({action: "<Chat key='" + key + "'> -> onSubmitNewMessage()", value: inputText.trim()}) }

    const attr1 = (attributes?.onSubmitMessage || attributes?.onMessage || attributes?.onsubmitmessage || attributes?.onmessage);
    if (attr1 && rootNamespace && rootNamespace[attr1]) {
      const clientFNC = rootNamespace[attr1];
      const recipients1 = rz.arrGetUnique((recipients || []).map((ii) => (ii.login || "")), true, true, true);
      if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: inputText.trim(), chat: newRows, openid, recipients: recipients1, safe});
        // Обработчик может быть синхронным или вернуть Promise, например после сохранения сообщения на сервере.
        // В обоих случаях применяем возвращенный state одинаково, чтобы результат был виден на странице.
        if (rz.isPromise(ret) || (ret && rz.isFunction(ret.then))) { ret.then(applyClientState).catch((err) => { console.log(err) }) }
        else { applyClientState(ret) }
      }
    }

    setInputText("");
  }, [elState.data, inputText, user, dispatch, rootAttributes.key, fullPath, attributes, rootNamespace, key, recipients, lng, dct, openid, safe, applyClientState]);

  const onPressEnter = useCallback((event:React.KeyboardEvent<HTMLInputElement | HTMLFormElement | HTMLTextAreaElement>) => {
    // @ts-ignore
    const {value} = event.target, {ctrlKey} = event;
    if (!ctrlKey || !chatType.enterWithCtrlToSend) { return }
    if (fnc.config?.isDev) console.log({action: "<Chat key='" + key + "'> -> onPressEnter()", value});
    // @ts-ignore
    onSubmitNewMessage();
  }, [chatType.enterWithCtrlToSend, key, onSubmitNewMessage]);

  const recipientsTitle = useMemo(() => (rz.strFromSelectedLng({lng: (lng as string), ru: "Получатели:", en: "Recipients:", tr: "Alıcılar:"})), [lng]);
  const buttonSendText = useMemo(() => (rz.strFromSelectedLng({lng: (lng as string), ru: "Отправить", en: "Send", tr: "Göndermek"})), [lng]);

  const avatarsGroup = useMemo(() => (
    recipients.map((ii:Tuser, i:number) => (
      <Avatar key={"avatar-" + (i + 1)} size="large" shape="circle"
        src={"/ui/avatar/" + ii.login} icon={<AppIcons icopath="fa/FaUserSecret" attrs={{}} />}
        uk-tooltip={rz.strFromSelectedLng({lng: (lng as string), ru: (ii.name_ru as string), en: (ii.name_en as string), def: (ii.login as string)})}
      />
    ))
  ), [lng, recipients]);

  const removeSelectedMessage = useCallback((msg) => {
    const i = rz.arrFindObj(elState.data || [], {text: msg.text, user: msg.user, strDate: msg.strDate});
    if (!~i) { notification.error({message: dct.e.somethingWrong, placement: "bottomRight"}); return }
    Modal.confirm({
      title: rz.strFromSelectedLng({lng: (lng as string), ru: "Удалить это сообщение?", en: "Delete this message?", tr: "Bu mesajı silin mi?"}),
      content: (<div key="dlg-cnt" className="txt-red uk-text-bold">{msg.text}</div>),
      onOk: () => {
        const newRows = rz.objClone(elState.data) || []; newRows.splice(i, 1);

        // @ts-ignore
        dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {data: newRows, loading: false, value: null}}));
        if (fnc.config?.isDev) { console.log({action: "<Chat key='" + key + "'> -> removeSelectedMessage()", message: msg}) }

        const attr1 = (attributes?.onRemoveMessage || attributes?.onremovemessage || attributes?.onDeleteMessage || attributes?.ondeletemessage);
        if (attr1 && rootNamespace && rootNamespace[attr1]) {
          const clientFNC = rootNamespace[attr1];
          const recipients1 = rz.arrGetUnique((recipients || []).map((ii) => (ii.login || "")), true, true, true);
          if (rz.isFunction(clientFNC)) {
            const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, chat: newRows, removedMessage: msg, openid, recipients: recipients1, safe});
            // Для удаления логика такая же, как для отправки: Chat уже поменял свой data,
            // а пользовательский обработчик может обновить статус, счетчик или другой видимый элемент.
            if (rz.isPromise(ret) || (ret && rz.isFunction(ret.then))) { ret.then(applyClientState).catch((err) => { console.log(err) }) }
            else { applyClientState(ret) }
          }
        }
      }
    });
  }, [attributes, dct, dispatch, elState.data, fullPath, key, lng, openid, recipients, rootAttributes.key, rootNamespace, safe, user, applyClientState]);

  const chatRows = useMemo(() => {
    // @ts-ignore
    const msgs:Array<MessageType> = (elState.data || []).map((ii, i) => {
      const usr = (allUsers.find((uu) => (uu.login?.toLowerCase() === (ii.user || "").toLowerCase())) || {login: ii.user, name_ru: "", name_en: ""});
      const msg = {
        id: "msg-" + (i + 1),
        type: rz.switchCase([
          ii.img, "photo",
          ii.file, "file",
          ii.type || "text"
        ]),
        position: (user.login?.toLowerCase() === ii.user.toLowerCase() ? "left" : "right"),
        avatar: "/ui/avatar/" + ii.user,
        title: (
          <div key={"msg-title-" + (i + 1)} className="rz-width-expand">
            <span key={"msg-name-" + (i + 1)} className="">{rz.strFromSelectedLng({lng: (lng as string), ru: (usr.name_ru as string), en: (usr.name_en as string)})}</span>
            <span key={"msg-close-" + (i + 1)} className="rz-margin-left-10">{
              !chatType.readOnly && (user.login?.toLowerCase() === ii.user?.toLowerCase() || rz.arrCompare(["admins"], userRoles, "includesAny"))
                ? (
                  // eslint-disable-next-line react/no-unknown-property
                  <a key={"msg-close-link-" + (i + 1)} href="#" uk-tooltip={rz.strFromSelectedLng({lng: (lng as string), ru: "Удалить сообщение", en: "Remove message", tr: "Mesajı Kaldır"})} onClick={() => { removeSelectedMessage(ii) }}>
                    <AppIcons key={"msg-close-icon-" + (i + 1)} icopath="fa/FaRegTrashAlt" color="red" />
                  </a>
                )
                : ""
            }</span>
          </div>
        ),
        date: rz.dateFromString(ii.strDate, "YYYY-MM-DD hh:mm:ss")
      };
      // @ts-ignore
      if (msg.type === "photo" || ii.img) { msg.data = {uri: ii.img} }
      // @ts-ignore
      if (msg.type === "file" || ii.file) { msg.data = {uri: ii.file} } // status: {click: false, loading: 0}
      // @ts-ignore
      if (ii.text) { msg.text = ii.text || "" }

      // @ts-ignore
      msg.className = "rz-wrap-text";

      return msg;
    });
    return msgs;
  }, [allUsers, chatType.readOnly, elState.data, lng, removeSelectedMessage, user.login, userRoles]);

  return (
    errMsg
      ? ErrorContent(errMsg)
      : (
        <div key={key} style={styl} className={clsu.join(" ")}>
          {/* Recipients List */}
          <div key={"recipients-grid-" + key} className="uk-grid rz-margin-top-5">
            <div key={"recipients-cell-1-" + key} className="uk-width-auto rz-flex rz-flex-middle rz-margin-right-10">
              <span key={"recipients-title-" + key} className="txt-warning-color txt-size-1em">{recipientsTitle}</span>
            </div>
            <div key={"recipients-cell-2-" + key} className="uk-width-expand">
              <Avatar.Group key={"avatar-group-" + key} size="large"
                max={{ count: 6, style: { color: "#f56a00", backgroundColor: "#fde3cf", cursor: "pointer" }, popover: { trigger: "click" } }}
              > {avatarsGroup} </Avatar.Group>
            </div>
          </div>
          {/* Input a new message Field */}
          <div key={"message-input-grid-" + key} className="uk-grid rz-margin-top-bottom-5" style={{display: (chatType.readOnly || permissions.disabled === true ? "none" : undefined)}} >
            <div key={"message-input-div-" + key} className="uk-width-expand">
              <Space.Compact key={"message-space-compact-" + key} style={{ width: "100%" }}>
                {chatType.type === "textarea"
                  ? (
                    <Input.TextArea
                      key={key} id={key} value={inputText}
                      placeholder={(attributes["placeholder_" + lng] || attributes.placeholder || attributes["placeholder_" + rlng] || "")}
                      // @ts-ignore
                      allowClear={chatType.allowClear} variant={chatType.variant} size={chatType.size} status={elState?.status || chatType.status}
                      autoSize={chatType.autoSize}
                      // maxLength разбирается из XML/type выше; передаем его в Ant TextArea,
                      // чтобы лимит ввода работал не только в state, но и в самом поле.
                      maxLength={chatType.maxLength}
                      rows={!chatType.autoSize && !attributes.rows ? 2 : (rz.isNumber(attributes.rows) ? attributes.rows : rz.strSearchNum(attributes.rows, 1, 0, true))}
                      // eslint-disable-next-line max-len
                      cols={!chatType.autoSize && !(attributes.cols || attributes.columns) ? undefined : (rz.isNumber(attributes.cols || attributes.columns) ? (attributes.cols || attributes.columns) : rz.strSearchNum((attributes.cols || attributes.columns), 1, 0, true))}
                      onChange={onInputChange} onPressEnter={onPressEnter} onBlur={onInputBlur}
                      disabled={!IamRecipient || elState?.disabled || permissions.disabled === true}
                      hidden={elState?.visible === false || permissions.visible === false}
                    />
                  ) : (
                    <Input
                      key={key} id={key} value={inputText}
                      placeholder={(attributes["placeholder_" + lng] || attributes.placeholder || attributes["placeholder_" + rlng] || "")}
                      // @ts-ignore
                      allowClear={chatType.allowClear} variant={chatType.variant} size={chatType.size} status={elState?.status || chatType.status}
                      // Такой же лимит нужен для compact input-режима.
                      maxLength={chatType.maxLength}
                      width={attributes.width || "100%"}
                      onChange={onInputChange} onPressEnter={onPressEnter} onBlur={onInputBlur}
                      disabled={!IamRecipient || elState?.disabled || permissions.disabled === true}
                      hidden={elState?.visible === false || permissions.visible === false}
                    />
                  )}
                  <Button key={"message-input-" + key} type="primary" style={{height: "auto"}} onClick={onSubmitNewMessage} uk-tooltip={buttonSendText} disabled={!IamRecipient || elState?.disabled || permissions.disabled === true}>
                    <AppIcons icopath="fa/FaTelegramPlane" attrs={{size: "1.6em"}} />
                  </Button>
              </Space.Compact>
            </div>
          </div>
          {/* Message List */}
          <div key={"message-list-div-" + key} className="rz-margin-top-5 uk-overflow-auto" style={{maxHeight: "350px", height: "auto"}}>
            <MessageList key={"message-list-" + key} className="message-list" lockable={true} toBottomHeight={"100%"} referance={messageListReferance} dataSource={chatRows} />
          </div>
        </div>
      )
  );
}

export default RZChat;
/* eslint-enable @typescript-eslint/no-unused-vars */
